import { useEffect, useState } from 'react';

import { dequal } from 'dequal';
import type { SWRConfiguration } from 'swr';
import useSWR from 'swr';

import type {
    AnalyticsData,
    AnalyticsParams,
    AnalyticsRecord
} from '@/utils/services/analytics/AnalyticsDataService';
import { AnalyticsDataService } from '@/utils/services/analytics/AnalyticsDataService';

import { useIsFirstStatsLoaded } from '@/hooks/socialprofiles/useIsFirstStatsLoaded';

import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { ANALYTICS_V2 } from '@/lib/routes/apiEndpoints';

type UseAnalyticsV2Options = SWRConfiguration & {
    enabled?: boolean;
};

type UseAnalyticsV2Data = {
    analytics: AnalyticsRecord;
    mutateAnalytics: () => void;
    analyticsError: Error | undefined;
    setRequestParams: (params: AnalyticsParams) => void;
    setState: (state: AnalyticsData | null) => void;
    isValidatingAnalytics: boolean;
    isLoading: boolean;
};

export const useAnalyticsV2 = (
    activeSPID: string | number | undefined,
    params: AnalyticsParams,
    options: UseAnalyticsV2Options = {}
): UseAnalyticsV2Data => {
    const { enabled = true, ...swrOptions } = options;

    const [firstLoad, setFirstLoad] = useState(false);
    const [state, setState] = useState<AnalyticsData>({});
    const [shouldReFetch, setShouldReFetch] = useState(false);
    const isFirstStatsLoaded = useIsFirstStatsLoaded({ SPID: activeSPID });

    // Keep comparison_period in formatted params for frontend logic
    const formattedParams = AnalyticsDataService.formatParamsForV2({
        params,
        keepComparisonPeriod: true
    });

    const [requestParams, setRequestParams] = useState<AnalyticsParams>(formattedParams);

    // Validate that required params are present before making request
    const hasRequiredParams =
        requestParams?.fields &&
        Array.isArray(requestParams.fields) &&
        requestParams.fields.length > 0 &&
        (requestParams.start || requestParams.album_id);

    const {
        data,
        mutate: mutateAnalytics,
        isValidating: isValidatingAnalytics,
        error: analyticsError
    } = useSWR(
        //the second part of this is needed to ensure that no unnecessary request is sent
        enabled &&
            isFirstStatsLoaded &&
            requestParams &&
            hasRequiredParams &&
            (dequal(formattedParams, requestParams) || shouldReFetch)
            ? [
                  injectEndpointUrlParams(ANALYTICS_V2, { SPID: activeSPID }),
                  ...Object.values(requestParams)
              ]
            : null,
        ([url]: [string]) => AnalyticsDataService.fetchAnalyticsV2(url, requestParams),
        {
            shouldRetryOnError: false,
            ...swrOptions
        }
    );

    // Sync from parent when period/dates/fields change. Same pattern as useAnalytics.js:
    // depend only on stable primitives (no params/formattedParams object) to avoid
    // running on every render and preserve user's widget parameter overrides (e.g. filter).
    useEffect(() => {
        if (!enabled) return;
        const updatedParams = AnalyticsDataService.formatParamsForV2({
            params,
            keepComparisonPeriod: true
        });
        if (dequal(updatedParams, requestParams)) {
            if (shouldReFetch) return setShouldReFetch(false);
            return;
        }
        setRequestParams((prev) => ({
            ...updatedParams,
            parameters:
                prev?.parameters != null
                    ? { ...updatedParams.parameters, ...prev.parameters }
                    : updatedParams.parameters
        }));
        setFirstLoad(false);
    }, [
        enabled,
        activeSPID,
        // Date-related params
        params?.period,
        params?.start,
        params?.end,
        params?.comparison_period,
        // Non-date params that affect formattedParams
        params?.fields,
        params?.parameters,
        params?.album_id,
        params?.disable_previous
    ]);

    // setShouldReFetch based on requestParams
    useEffect(() => {
        if (!enabled) return;
        if (dequal(formattedParams, requestParams)) {
            // switch back shouldRefresh to false
            if (shouldReFetch) return setShouldReFetch(false);
            return;
        }
        //make sure we don't set shouldReFetch if it's not needed
        if (shouldReFetch) return;
        setShouldReFetch(true);
    }, [enabled, requestParams]);

    useEffect(() => {
        if (!enabled) return;
        if (!data?.analytics && !analyticsError) {
            return;
        }
        setFirstLoad(true);
        setState(data ?? {});
    }, [enabled, analyticsError, data?.analytics]);

    return {
        analytics: state?.analytics ?? {},
        mutateAnalytics,
        analyticsError,
        setRequestParams,
        setState,
        isValidatingAnalytics,
        isLoading: (!state?.analytics && !analyticsError) || !firstLoad
    };
};
